import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, ButtonBase, CircularProgress, InputAdornment, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { KeyboardReturn, PersonSearchOutlined, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { peopleService, Person } from '../../services/peopleService';
import { useDebounce } from '../../hooks/useDebounce';
import PersonAvatar from '../../components/common/PersonAvatar';
import { ToneChip } from '../../components/common/StatusChip';
import { PERSON_KIND_LABEL, PERSON_KIND_TONE } from '../../components/families/familyFormat';
import { profilePath } from '../../utils/personRef';

/**
 * Find anyone — applicant, student or alumni — by name, GR number,
 * application number, CNIC or phone, and open their profile. Arrow keys
 * move, Enter opens. A typed GR number (leading zeros optional) can be
 * opened straight away.
 */

const asGrNumber = (q: string): string | null => {
  const digits = q.trim();
  return /^\d{1,6}$/.test(digits) && Number(digits) > 0 ? digits.padStart(6, '0') : null;
};

const familyLabel = (p: Person) => (typeof p.familyId === 'object' ? p.familyId?.displayName : undefined);

/** A person found, or a typed GR number to open directly. */
type Option = { key: string; path: string; gr?: string; person?: Person };

const StudentLookupPage: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query.trim(), 250);
  const [results, setResults] = useState<Person[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    if (debounced.length < 2) { setResults([]); setError(null); return; }
    const id = ++latest.current;
    setSearching(true);
    peopleService.listPeople({ q: debounced, limit: 8 })
      .then(r => { if (id === latest.current) { setResults(r.items); setError(null); } })
      .catch((err: any) => { if (id === latest.current) { setResults([]); setError(err?.message || 'Search failed'); } })
      .finally(() => { if (id === latest.current) setSearching(false); });
  }, [debounced]);

  // A typed GR number leads the list unless the search already found that person.
  const options = useMemo<Option[]>(() => {
    const gr = asGrNumber(query);
    const found = results.map(p => ({ key: p._id, path: profilePath(p), person: p }));
    return gr && !results.some(p => p.grNumber === gr) ? [{ key: `gr-${gr}`, path: profilePath({ _id: gr, grNumber: gr }), gr }, ...found] : found;
  }, [query, results]);

  useEffect(() => { setActive(0); }, [options]);

  const open = (o?: Option) => { if (o) navigate(o.path); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, options.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') open(options[active]);
  };

  const searched = debounced.length >= 2 && !searching;

  return (
    <Box sx={{ maxWidth: 680, mx: 'auto', pt: { xs: 2, md: 6 } }}>
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Student lookup</Typography>
        <Typography variant="body2" color="text.secondary">
          Find an applicant, student or alumnus by name, GR number, application number, CNIC or phone.
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TextField
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search by name, GR, application no., CNIC or phone"
          autoFocus
          fullWidth
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: { px: 2.5, py: 1.75, fontSize: 17 },
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
            ) : undefined,
          }}
          inputProps={{ 'aria-label': 'Search people', role: 'combobox', 'aria-expanded': options.length > 0 }}
        />

        {options.length > 0 && (
          <Box role="listbox" sx={{ borderTop: 1, borderColor: 'divider', py: 0.75 }}>
            {options.map((o, i) => (
              <ButtonBase
                key={o.key}
                role="option"
                aria-selected={i === active}
                onClick={() => open(o)}
                onMouseEnter={() => setActive(i)}
                sx={{
                  display: 'flex', width: '100%', textAlign: 'left', gap: 1.75, px: 2.5, py: 1.25,
                  bgcolor: i === active ? 'action.hover' : 'transparent',
                }}
              >
                {o.person ? (
                  <>
                    <PersonAvatar name={o.person.name} photoFilename={o.person.photoFilename} size={40} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{o.person.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[
                          o.person.grNumber && <Box key="gr" component="span" sx={{ fontFamily: 'monospace' }}>GR {o.person.grNumber}</Box>,
                          familyLabel(o.person) && `${familyLabel(o.person)} family`,
                        ].filter(Boolean).flatMap((part, j) => (j ? [' · ', part] : [part]))}
                      </Typography>
                    </Box>
                    <ToneChip label={PERSON_KIND_LABEL[o.person.kind]} tone={PERSON_KIND_TONE[o.person.kind]} />
                  </>
                ) : (
                  <>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'action.selected' }}>
                      <PersonSearchOutlined fontSize="small" color="action" />
                    </Box>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Open <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GR {o.gr}</Box>
                    </Typography>
                  </>
                )}
                {i === active && <KeyboardReturn fontSize="small" sx={{ color: 'text.disabled' }} />}
              </ButtonBase>
            ))}
          </Box>
        )}

        {searched && options.length === 0 && (
          <Typography variant="body2" color={error ? 'error' : 'text.secondary'} sx={{ borderTop: 1, borderColor: 'divider', px: 2.5, py: 2.5 }}>
            {error || `No one matches “${debounced}”.`}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default StudentLookupPage;
